// Shared primitives for the Fuel Register Reconciliation module.
//
// Everything here is deterministic and side-effect free. Three rules drive it:
//
//   1. QUANTITIES ARE INTEGER CENTILITRES.
//      Every comparison, sum and equality test in the reconciliation runs on
//      integers, never on floats. 0.1 + 0.2 !== 0.3 in IEEE-754, and a
//      reconciliation that reports a phantom 0.000000004 L difference is worse
//      than useless. Litres are converted once at the boundary (toCl) and back
//      once for display (fromCl).
//
//   2. MISSING IS NOT ZERO.
//      parseQuantity / parseNumber return a { value, state } pair so a blank
//      cell (EMPTY) is never collapsed into a real 0 (ZERO). Callers must keep
//      the two apart all the way to the screen.
//
//   3. LABEL KEYS FOLD COSMETICS ONLY — THIS IS NOT FUZZY MATCHING.
//      labelKeyOf delegates to the shared normalizeKey, which folds case and
//      separator punctuation and nothing else. "EX-16", "EX.16" and "ex 16"
//      share a key; "EX16" and "EX-17" do NOT. A register label still only
//      resolves to an asset through a human-CONFIRMED alias document — the key
//      merely decides which alias document a spelling looks up.

import moment from 'moment';
import { classifyCell } from '../textNormalize';

export const CL_PER_L = 100;

/** Litres -> integer centilitres. Returns null for a missing/non-finite value. */
export const toCl = (litres) => {
  if (litres === null || litres === undefined) return null;
  const n = Number(litres);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * CL_PER_L);
};

/** Integer centilitres -> litres. Returns null for a missing value. */
export const fromCl = (cl) => {
  if (cl === null || cl === undefined) return null;
  const n = Number(cl);
  if (!Number.isFinite(n)) return null;
  return n / CL_PER_L;
};

/** Sum a list of centilitre values, ignoring nulls. Returns an integer. */
export const sumCl = (values) =>
  values.reduce((acc, v) => (v === null || v === undefined ? acc : acc + v), 0);

/**
 * Strict numeric parse for register capture and import.
 * Accepts digits, one decimal point, an optional sign and thousands separators.
 * REJECTS anything containing a letter — "12 ltr" is a transcription that needs
 * a human, not a number we silently strip units off.
 *
 * @returns {{ value:number|null, state:'OK'|'EMPTY'|'INVALID' }}
 */
export const parseNumber = (raw) => {
  const cell = classifyCell(raw);
  if (cell === 'EMPTY') return { value: null, state: 'EMPTY' };
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { value: raw, state: 'OK' } : { value: null, state: 'INVALID' };
  }
  const s = String(raw).trim().replace(/,/g, '');
  if (s === '' || s === '-' || s === '--') return { value: null, state: 'EMPTY' };
  if (!/^-?\d+(\.\d+)?$/.test(s)) return { value: null, state: 'INVALID' };
  const n = Number(s);
  return Number.isFinite(n) ? { value: n, state: 'OK' } : { value: null, state: 'INVALID' };
};

/**
 * Parse a litre quantity into centilitres.
 * @returns {{ cl:number|null, litres:number|null, state:'OK'|'EMPTY'|'INVALID'|'NEGATIVE' }}
 */
export const parseQuantity = (raw) => {
  const { value, state } = parseNumber(raw);
  if (state !== 'OK') return { cl: null, litres: null, state };
  if (value < 0) return { cl: null, litres: value, state: 'NEGATIVE' };
  return { cl: toCl(value), litres: value, state: 'OK' };
};

// --- day keys ---------------------------------------------------------------
//
// The register books a calendar day at site; AssetPro stores a timestamp. The
// whole module keys on the LOCAL calendar day of that timestamp, matching every
// other date rendering in this app. A shift entry recorded at 23:50 on the 22nd
// that the register books on the 23rd is therefore a genuine cut-off difference
// — which is exactly what the CC_DATE_SHIFT cross-check and detector D6 exist
// to surface, rather than something to paper over here.

export const DAY_FMT = 'YYYY-MM-DD';

/** Epoch ms -> 'YYYY-MM-DD' (local). Returns null when the input is unusable. */
export const dayKeyFromMs = (ms) => {
  if (ms === null || ms === undefined) return null;
  const m = moment(ms);
  return m.isValid() ? m.format(DAY_FMT) : null;
};

/** Any moment-parseable date -> 'YYYY-MM-DD'. Returns null when unparseable. */
export const dayKeyOf = (value) => {
  if (!value) return null;
  const m = moment(value, [DAY_FMT, moment.ISO_8601], true);
  if (m.isValid()) return m.format(DAY_FMT);
  const loose = moment(value);
  return loose.isValid() ? loose.format(DAY_FMT) : null;
};

/** Every calendar day from `from` to `to` inclusive, as 'YYYY-MM-DD' strings. */
export const enumerateDays = (from, to) => {
  const start = moment(from).startOf('day');
  const end = moment(to).startOf('day');
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];
  const out = [];
  const cur = start.clone();
  // Hard bound: a query wider than ~5.5 years is a mistake, not a report.
  for (let i = 0; cur.isSameOrBefore(end) && i < 2000; i += 1) {
    out.push(cur.format(DAY_FMT));
    cur.add(1, 'day');
  }
  return out;
};

export const dayDiff = (a, b) => moment(a, DAY_FMT).diff(moment(b, DAY_FMT), 'days');

// --- scope key -------------------------------------------------------------
//
// A declaration belongs to one period AND one filter scope. The same month at a
// different location is a different reconciliation, so the scope forms part of
// the key rather than being ignored.

export const scopeKeyOf = ({ fuelType, locations = [], subLocations = [], storages = [] } = {}) =>
  [
    fuelType || 'all',
    locations.slice().sort().join('+') || 'all',
    subLocations.slice().sort().join('+') || 'all',
    storages.slice().sort().join('+') || 'all',
  ]
    .join('~')
    .replace(/[^A-Za-z0-9~+_-]/g, '_');

export const periodKeyOf = (from, to) =>
  `${moment(from).format('YYYYMMDD')}-${moment(to).format('YYYYMMDD')}`;
